import React, { useCallback, useEffect, useRef, useState } from "react";
import { getProductsAPI } from "../../../utils/api";
import { UI_CONSTANTS } from "../../../utils/configConstants";
import "./ProductSearchSelect.css";

const PRODUCT_PAGE_LIMIT = 50;

const ProductSearchSelect = ({
  selectedProduct,
  onSelect,
  disabled = false,
  optional = false,
  label = "Linked product",
  className = "",
  /** When true, only products with active truthy (active products) appear in the list */
  onlyActiveProducts = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  const displayName =
    selectedProduct?.name != null
      ? String(selectedProduct.name).trim()
      : "";

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, UI_CONSTANTS?.DEBOUNCE_DELAY ?? 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const fetchProducts = useCallback(async (search) => {
    setLoading(true);
    setListError(null);
    try {
      const response = await getProductsAPI({
        category_id: null,
        search: search || "",
        page: 1,
        limit: PRODUCT_PAGE_LIMIT,
        active:1,
      });
      if (response?.status === 200) {
        let list = Array.isArray(response.data) ? response.data : [];
        if (onlyActiveProducts) {
          list = list.filter((p) => {
            if (!p || typeof p !== "object") return false;
            const a = p.active;
            return a === true || a === 1 || a === "1";
          });
        }
        setOptions(list);
      } else {
        setOptions([]);
        setListError(
          typeof response?.message === "string"
            ? response.message
            : "Could not load products"
        );
      }
    } catch {
      setOptions([]);
      setListError("Could not load products");
    } finally {
      setLoading(false);
    }
  }, [onlyActiveProducts]);

  useEffect(() => {
    if (!open) return;
    fetchProducts(debouncedSearch);
  }, [open, debouncedSearch, fetchProducts]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (selectedProduct?.name) {
      setSearchInput("");
    }
  }, [selectedProduct]);

  return (
    <div
      className={`product-search-select${className ? ` ${className}` : ""}`.trim()}
      ref={wrapRef}
      data-open={open ? "true" : "false"}
    >
      <label className="input-title">
        {label}
        {optional ? (
          <span className="product-search-select__optional-hint"> (optional)</span>
        ) : (
          <span className="required-asterisk">*</span>
        )}
      </label>
      <button
        type="button"
        className="product-search-select__trigger"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((o) => !o);
          }
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span
          className={
            displayName
              ? "product-search-select__trigger-text"
              : "product-search-select__trigger-placeholder"
          }
        >
          {displayName || "Select product"}
        </span>
        <span className="product-search-select__chevron" aria-hidden />
      </button>

      {open && (
        <div className="product-search-select__dropdown" role="listbox">
          <input
            type="text"
            className="product-search-select__search"
            placeholder="Search products…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            disabled={disabled}
            aria-label="Search products"
          />
          {loading && (
            <div className="product-search-select__status">Loading…</div>
          )}
          {!loading && listError && (
            <div className="product-search-select__status product-search-select__status--error">
              {listError}
            </div>
          )}
          {!loading && !listError && options.length === 0 && (
            <div className="product-search-select__status">
              No products match your search.
            </div>
          )}
          <ul className="product-search-select__list">
            <li>
              <button
                type="button"
                className="product-search-select__option product-search-select__option--clear"
                onClick={() => {
                  onSelect(null);
                  setSearchInput("");
                  setOpen(false);
                }}
              >
                Select product
              </button>
            </li>
            {options.map((p) => (
              <li key={p._id}>
                <button
                  type="button"
                  className="product-search-select__option"
                  onClick={() => {
                    onSelect({ _id: p._id, name: p.name || "" });
                    setSearchInput("");
                    setOpen(false);
                  }}
                >
                  {p.name || "(Unnamed product)"}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProductSearchSelect;
